import { Constants, InsertionOrderUtils } from '@bingads-webui-clientcenter/entity-utils';
import { ClickChangeType,
  InsertionOrderFieldNameTextKey,
  InsertionOrderStatusTextKey,
  InsertionOrderTextKey,
  LifetimeBudgetField,
  TooltipChartKeyDataTitleKey,
  TooltipChartType } from '../constants';
import { formatCurrency, formatDate, formatInteger, parseDecimal } from './format-utils';

const {
  BudgetEventType, BudgetChangeType, InsertionOrdersGridFieldNames,
} = Constants;
const { isEndlessIOEndDate, isUnlimitedIOBudget } = InsertionOrderUtils;

export function getKeyDataItemsConfig({ i18n, chartType }) {
  switch (chartType) {
    case TooltipChartType.AccountPastDayDailySpend:
    case TooltipChartType.AccountTodayDailySpend:
      return [{
        title: i18n.getString(TooltipChartKeyDataTitleKey.SpendOfTheDay),
        valueFieldName: 'DailySpend',
      }];
    case TooltipChartType.AccountPastDayCumulativeSpend:
    case TooltipChartType.AccountTodayCumulativeSpend:
      return [{
        title: i18n.getString(TooltipChartKeyDataTitleKey.SpendOfTheDay),
        valueFieldName: 'DailySpend',
      }, {
        title: i18n.getString(TooltipChartKeyDataTitleKey.MonthToDateSpend),
        valueFieldName: 'MTDSpend',
      }];
    case TooltipChartType.CampaignPastDayDailySpend:
    case TooltipChartType.CampaignTodayDailySpend:
      return [{
        title: i18n.getString(TooltipChartKeyDataTitleKey.SpendOfTheDay),
        valueFieldName: 'DailySpend',
      }, {
        title: i18n.getString(TooltipChartKeyDataTitleKey.LifetimeBudget),
        valueFieldName: 'LifetimeBudget',
      }, {
        title: i18n.getString(TooltipChartKeyDataTitleKey.DailyBudget),
        valueFieldName: 'DailyBudget',
      }];
    case TooltipChartType.CampaignPastDayCumulativeSpend:
    case TooltipChartType.CampaignTodayCumulativeSpend:
      return [{
        title: i18n.getString(TooltipChartKeyDataTitleKey.SpendOfTheDay),
        valueFieldName: 'DailySpend',
      }, {
        title: i18n.getString(TooltipChartKeyDataTitleKey.MonthToDateSpend),
        valueFieldName: 'MTDSpend',
      }, {
        title: i18n.getString(TooltipChartKeyDataTitleKey.LifetimeBudget),
        valueFieldName: 'LifetimeBudget',
      }, {
        title: i18n.getString(TooltipChartKeyDataTitleKey.MonthlySpendLimit),
        valueFieldName: 'MonthlyBudget',
      }];
    default:
      return [];
  }
}

export const formatTooltipEventType = ({
  i18n, eventType,
}) => {
  switch (eventType) {
    case BudgetEventType.CampaignOutOfBudget:
      return { message: i18n.getString(_TL_('Campaign out of budget')) };
    case BudgetEventType.PrepayAccountBalanceZero:
      return { message: i18n.getString(_TL_('Account out of budget')) };
    case BudgetEventType.InsertionOrderBalanceDepleted:
      return { message: i18n.getString(_TL_('Insertion order exhausted')) };
    case BudgetEventType.InsertionOrderExpired:
      return { message: i18n.getString(_TL_('Insertion order expired')) };
    case BudgetEventType.AccountHold:
      return { message: i18n.getString(_TL_('Payment declined')) };
    default:
      return null;
  }
};

function formatInsertionOrderValue({
  fieldName, value, i18n, currencyCode,
}) {
  if (fieldName === InsertionOrdersGridFieldNames.SpendCapAmount) {
    return isUnlimitedIOBudget(Number(value))
      ? i18n.getString(InsertionOrderTextKey.UnlimitedLowerCase)
      : formatCurrency({ i18n, value: parseDecimal({ i18n, value }), currencyCode });
  }
  if (fieldName === InsertionOrdersGridFieldNames.Status) {
    return i18n.getString(InsertionOrderStatusTextKey[Number(value)]);
  }
  if (fieldName === InsertionOrdersGridFieldNames.ThruDate) {
    const endDate = new Date(value);
    return isEndlessIOEndDate(endDate)
      ? i18n.getString(InsertionOrderTextKey.NoEndDateLowerCase)
      : formatDate({ i18n, dateString: endDate });
  }
  return value;
}

function getInsertionOrderMessage({ i18n, eventHistory, currencyCode }) {
  if (!eventHistory.OldValue) {
    return i18n.getString(_TL_('Order was created'));
  }
  return i18n.getString(
    _TL_('Insertion order {{changeField}} changed from {{oldValue}} to {{newValue}}'),
    {
      changeField: i18n.getString(InsertionOrderFieldNameTextKey[eventHistory.FieldName]),
      oldValue: formatInsertionOrderValue({
        fieldName: eventHistory.FieldName, value: eventHistory.OldValue, i18n, currencyCode,
      }),
      newValue: formatInsertionOrderValue({
        fieldName: eventHistory.FieldName, value: eventHistory.NewValue, i18n, currencyCode,
      }),
    }
  );
}

export const formatTooltipBudgetChangeType = ({
  i18n, eventType, eventHistory, currencyCode,
}) => {
  switch (eventType) {
    case BudgetChangeType.PrepayBalanceRecharge:
      return {
        message: i18n.getString(
          _TL_('Balance recharged with {{changeAmount}}'),
          { changeAmount: formatCurrency({ i18n, value: eventHistory.NewValue, currencyCode }) }
        ),
      };
    case BudgetChangeType.InsertionOrderChange:
      return {
        message: getInsertionOrderMessage({ i18n, eventHistory, currencyCode }),
      };
    case BudgetChangeType.ThresholdPaymentChange:
      return {
        message: i18n.getString(
          _TL_('Payment of {{changeAmount}} made'),
          { changeAmount: formatCurrency({ i18n, value: eventHistory.NewValue, currencyCode }) }
        ),
      };
    case BudgetChangeType.CampaignDailyBudgetChange:
      return {
        message: i18n.getString(
          eventHistory.FieldName === LifetimeBudgetField
            ? _TL_('Lifetime budget changed from {{oldValue}} to {{newValue}}')
            : _TL_('Daily budget changed from {{oldValue}} to {{newValue}}'),
          {
            oldValue: formatCurrency({ i18n, value: parseDecimal({ i18n, value: eventHistory.OldValue }), currencyCode }),
            newValue: formatCurrency({ i18n, value: parseDecimal({ i18n, value: eventHistory.NewValue }), currencyCode }),
          }
        ),
        hint: eventHistory.DailyClickChange !== 0 && {
          type: eventHistory.DailyClickChange > 0 ? ClickChangeType.Increase : ClickChangeType.Decrease,
          clicks: formatInteger({ i18n, value: eventHistory.DailyClickChange }),
        },
      };
    default:
      return null;
  }
};
